import { autobind, debounce } from "@use/decorators";

import { Collection, CollectionModel } from "./Collection";
import { ObservableModel } from "./ObservableModel";

/**
 * Specification of observer callbacks, which will be triggered whenever
 * internal data on the model changes.
 */
type Observer = () => void;

/**
 * We're using this symbol to safely store the list of observers subscribed to
 * collection instances without risking namespace collisions.
 */
const observers = Symbol("ObservableCollection.observers");

/**
 * We're using this symbol to safely store the disposer returned from mobx, when
 * we began observing the collection for changes.
 */
const isObserving = Symbol("ObservableCollection.isObserving");

/**
 * An observable collection provides a mechanism to subscribe to data changes,
 * both of the collection itself, and within each of the observable models held
 * within it.
 */
export abstract class ObservableCollection<
  T extends CollectionModel
> extends Collection<T> {
  /**
   * Contains the list of currently applied observers, which will be notified
   * about any changes in our data model.
   */
  private [observers]: Set<Observer> = new Set();

  /**
   * Specifies if we're currently observing the collection for changes, in
   * which case we're going to have to subscribe to deep changes whenver
   * models are being added to the collection.
   */
  private [isObserving]?: boolean;

  /**
   * Publicly expose a variable, which we can use to determine if the model
   * is currently being observed.
   */
  public get isObserved(): boolean {
    return !!this[isObserving];
  }

  /**
   * Extend the add-metod, making sure we're going to observe added observable
   * models.
   */
  public add(item: T, index?: number): void {
    // Begin observing this item for changes
    if (this[isObserving]) {
      if (
        item instanceof ObservableModel ||
        item instanceof ObservableCollection
      ) {
        item.observe(this.emitChange);
      }
    }

    // Add item to collection
    super.add(item, index);

    // Emit a change event immediately
    this.emitChange();
  }

  /**
   * Extend the move-metod, making sure we're going to emit a change event
   * after having moved the item.
   */
  public move(item: T, index: number): void {
    super.move(item, index);

    // Emit a change event immediately
    this.emitChange();
  }

  /**
   * Extend the remove-metod, making sure we stop observing items when they're
   * removed from the collection.
   */
  public remove(item: T): void {
    // Stop observing this item for changes
    if (this[isObserving]) {
      if (
        item instanceof ObservableModel ||
        item instanceof ObservableCollection
      ) {
        item.unobserve(this.emitChange);
      }
    }

    // Remove item from collection
    super.remove(item);

    // Emit a change event immediately
    this.emitChange();
  }

  /**
   * Helper that'll subscribe the given callback to data change events, so
   * that it can easily interact to any changes made.
   */
  public observe(callback: Observer): void {
    // If we're not already observing the collection, then we'll need to
    // iterate over all contained items and begin observation for nested
    // observable models
    if (!this[isObserving]) {
      this.forEach((item) => {
        if (
          item instanceof ObservableModel ||
          item instanceof ObservableCollection
        ) {
          item.observe(this.emitChange);
        }
      });
    }

    // Register that we're now observing for changes
    this[isObserving] = true;

    // Subscribe the callback to change events
    this[observers].add(callback);
  }

  /**
   * Helper that'll unsubscribe the given callback from data change events.
   */
  public unobserve(callback: Observer): void {
    this[observers].delete(callback);

    // Update state, we're only observing the model if there are any
    // observers left
    this[isObserving] = this[observers].size > 0;

    // If all observers have been removed, then we'll need to stop observing
    // nested changes
    if (!this[isObserving]) {
      this.forEach((item) => {
        if (
          item instanceof ObservableModel ||
          item instanceof ObservableCollection
        ) {
          item.unobserve(this.emitChange);
        }
      });
    }
  }

  /**
   * Internal helper that's used to actually emit changes
   */
  @autobind()
  @debounce(1)
  private emitChange(): void {
    for (const observer of this[observers]) {
      observer();
    }
  }
}
